import { Route, Routes } from '@angular/router';
import { SecurityLoaders } from '@cfg/security/loaders';
import { HomeComponent } from './handbook/pages/home/home.component';
import { DashboardComponent } from './dashboard/dashboard.component';
import { FinalTestComponent } from '../final-test.component';
import { TestUnifiedLibraryComponent } from '../test-unified-library.component';
import { SimpleTestComponent } from '../simple-test.component';
import { PersonasListComponent } from './personas/personas-list.component';
import { AdministrarEmpleadosComponent } from './empleados/administrar-empleados/container/administrar-empleados.component';
import { ServiciosSapComponent } from './servicios-sap/container/servicios-sap.component';
import { MetasComponent } from './metas/container/metas.component';
import { AgendaComponent } from './agenda/administrar-agenda/container/administrar-agenda.component';
import { OrdenesServicioComponent } from './ordenes-servicio/ordenes-servicio.component';
import { ProgramacionMasivaComponent } from './agenda/programacion-masiva/container/programacion-masiva.component';
import { CumplimientosProgramadosComponent } from './cumplimientos/container/cumplimientos-programados.component';
import { RiesgosPotencialesComponent } from './cumplimientos/container/riesgos-potenciales.component';
import { SeguimientoRiesgoPotencialComponent } from './cumplimientos/container/seguimiento-riesgo-potencial.component';
import { BusquedaOrdenesComponent } from './orientaciones-no-programadas/busqueda-ordenes.component';
import { SinOrientacionServiciosComponent } from './sin-orientacion-servicios/sin-orientacion-servicios.component';
import { InformesRechazadosComponent } from './informes-rechazados/informes-rechazados.component';
import { OrientacionesProgramadasComponent } from './informes-rechazados/orientaciones-programadas/orientaciones-programadas.component';
import { OrientacionesNoProgramadasComponent } from './informes-rechazados/orientaciones-no-programadas/orientaciones-no-programadas.component';
import { DevolucionCumplimientosComponent } from './devolucion-cumplimientos/devolucion-cumplimientos.component';
import { ResultadoCargaMasivaComponent } from './cumplimiento/resultado-carga-masiva.component';
import { AccessDeniedComponent } from './shared/pages/access-denied/access-denied.component';
import { NotFoundComponent } from './shared/pages/not-found/not-found.component';
import { CancelacionSitioComponent } from './cancelacion-sitio/cancelacion-sitio.component';
import { DetalleCancelacionSitioComponent } from './cancelacion-sitio/detalle/detalle-cancelacion-sitio.component';
import { CambioOrientacionComponent } from './cambio-orientacion/cambio-orientacion.component';
import { AprobarRechazarInformesComponent } from './aprobar-rechazar-informes/aprobar-rechazar-informes.component';
import { ServiciosEntregadosComponent } from './servicios-entregados/servicios-entregados.component';
import { InformacionEmpresaComponent } from './informacion-empresa/informacion-empresa.component';
import { InformacionEmpresaHistorialGestionesComponent } from './informacion-empresa/informacion-empresa-historial-gestiones.component';
import { InformacionEmpresaOrdenadoConsultoriaComponent } from './informacion-empresa/informacion-empresa-ordenado-consultoria.component';
import { InformacionEmpresaDetalleOrdenadoConsultoriaComponent } from './informacion-empresa/informacion-empresa-detalle-ordenado-consultoria.component';
import { InformacionEmpresaCitasProgramadasComponent } from './informacion-empresa/informacion-empresa-citas-programadas.component';
import { InformacionEmpresaDetalleCitasProgramadasComponent } from './informacion-empresa/informacion-empresa-detalle-citas-programadas.component';
import { InformacionEmpresaServiciosEntregadosComponent } from './informacion-empresa/informacion-empresa-servicios-entregados.component';
import { InformacionEmpresaDetalleServiciosEntregadosComponent } from './informacion-empresa/informacion-empresa-detalle-servicios-entregados.component';
import { InformacionEmpresaInformesEntregadosComponent } from './informacion-empresa/informacion-empresa-informes-entregados.component';
import { InformacionEmpresaDetalleInformesEntregadosComponent } from './informacion-empresa/informacion-empresa-detalle-informes-entregados.component';
import { InformacionTareasComponent } from './informacion-tareas/container/informacion-tareas.component';
import { InformacionTareasDetalleComponent } from './informacion-tareas/container/informacion-tareas-detalle.component';
import { LayoutComponent } from './shared/templates/layout/layout.component';

const SECURITY_GUARDS = SecurityLoaders.loadGuards() as NonNullable<
  Route['canActivate']
>;
const PUBLIC_ROUTE_PATHS = new Set(['', 'access-denied', 'not-found']);

function mergeCanActivateGuards(
  route: Route,
  securityGuards: NonNullable<Route['canActivate']>
): Route {
  if (
    route.redirectTo ||
    !securityGuards.length ||
    PUBLIC_ROUTE_PATHS.has(route.path ?? '')
  ) {
    return route;
  }

  const existingGuards = route.canActivate ?? [];
  const canActivate: NonNullable<Route['canActivate']> = [];

  for (const guard of securityGuards) {
    if (!canActivate.includes(guard)) {
      canActivate.push(guard);
    }
  }

  for (const guard of existingGuards) {
    if (!canActivate.includes(guard)) {
      canActivate.push(guard);
    }
  }

  return {
    ...route,
    canActivate
  };
}

function protectRoutes(
  routes: Routes,
  securityGuards: NonNullable<Route['canActivate']>
): Routes {
  return routes.map((route) => {
    const withChildren = route.children
      ? {
          ...route,
          children: protectRoutes(route.children, securityGuards)
        }
      : route;

    return mergeCanActivateGuards(withChildren, securityGuards);
  });
}

export function buildAppRoutes(
  securityGuards: NonNullable<Route['canActivate']> = SECURITY_GUARDS
): Routes {
  return protectRoutes(BASE_ROUTES, securityGuards);
}

const PRIVATE_ROUTES: Routes = [
  {
    path: 'dashboard',
    title: '🚀 GIROP Dashboard',
    component: DashboardComponent
  },
  // POC: Administración de Personas
  {
    path: 'personas',
    title: '👤 Administración de Personas',
    component: PersonasListComponent
  },
  // HU1259115: Administrar Empleados/Consultores (Talento Humano)
  {
    path: 'empleados',
    title: '👤 Administrar Empleados/Consultores',
    component: AdministrarEmpleadosComponent
  },
  // HU1287272: Administración › Maestro › Servicios SAP
  {
    path: 'servicios-sap',
    title: '🏷️ Servicios SAP',
    component: ServiciosSapComponent
  },
  // HU1290096: Administración › Maestros › Metas (Asignación Masiva de Metas Mes)
  {
    path: 'metas',
    title: '🎯 Asignación Masiva de Metas Mes',
    component: MetasComponent
  },
  // POC: Administrar Agenda (Migración desde SchedulingServicesCalendar/CrearCitaAdministrativaView)
  {
    path: 'agenda',
    title: '📅 Administrar Agenda',
    component: AgendaComponent
  },
  // POC: Programar Servicios (Migración desde FilterProgramarServiciosViewImpl)
  {
    path: 'ordenes-servicio',
    title: '📋 Programar Servicios',
    component: OrdenesServicioComponent
  },
  // Orientaciones No Programadas (Migración desde FilterOrdenesNoProgActivity)
  {
    path: 'orientaciones-no-programadas',
    title: 'Orientaciones No Programadas',
    component: BusquedaOrdenesComponent
  },
  // Sin Orientación de Servicios (Migración desde CumplimientoSinOcViewImpl.java)
  {
    path: 'sin-orientacion-servicios',
    title: 'Sin Orientación de Servicios',
    component: SinOrientacionServiciosComponent
  },
  // Programación Masiva de Citas (migrado de ProgramacionMasivaViewImpl.java)
  {
    path: 'agenda/programacion-masiva',
    title: '📋 Programación Masiva',
    component: ProgramacionMasivaComponent
  },
  // Devolución de Cumplimientos (Migración desde DevolucionCumplimientosViewImpl.java)
  {
    path: 'devolucion-cumplimientos',
    title: 'Devolución de Cumplimientos',
    component: DevolucionCumplimientosComponent
  },
  // Cumplimientos Programados (migrado de FilterOrdenesProgViewImpl.java)
  {
    path: 'orientacionesProgramadas',
    title: '✅ Orientaciones Programadas',
    component: CumplimientosProgramadosComponent
  },
  // Riesgos Potenciales / Alertas Tempranas (migrado de RiesgoPotencialViewImpl.java)
  {
    path: 'riesgos-potenciales',
    title: '⚠️ Riesgos Potenciales',
    component: RiesgosPotencialesComponent
  },
  {
    path: 'riesgos-potenciales/:id/seguimiento',
    title: '📋 Seguimiento de Riesgo Potencial',
    component: SeguimientoRiesgoPotencialComponent
  },
  // Informes Rechazados (Migración desde FilterCumplimProgViewImpl / FilterCumplimNoProgViewImpl)
  {
    path: 'informes-rechazados',
    title: '📋 Informes Rechazados',
    component: InformesRechazadosComponent,
    children: [
      {
        path: 'orientaciones-programadas',
        title: '📅 Orientaciones Programadas',
        component: OrientacionesProgramadasComponent
      },
      {
        path: 'orientaciones-no-programadas',
        title: '📝 Orientaciones No Programadas',
        component: OrientacionesNoProgramadasComponent
      }
    ]
  },
  // Resultado Carga Masiva — Cumplimientos Sin OC (HU1132426)
  {
    path: 'cumplimiento/resultado-carga-masiva',
    title: 'Resultado Carga Masiva',
    component: ResultadoCargaMasivaComponent
  },
  // Cancelaciones en Sitio (Migración desde CancelacionSitioNoEnviadasViewImpl)
  {
    path: 'cancelaciones-sitio',
    title: '💰 Cancelaciones en Sitio',
    component: CancelacionSitioComponent
  },
  // Detalle de Cancelación en Sitio (Migración desde DetalleCancelacionSitioViewImpl)
  {
    path: 'cancelaciones-sitio/:id',
    title: '📋 Detalle Cancelación',
    component: DetalleCancelacionSitioComponent
  },
  // Cambios en Orientaciones (Migración desde RechazarOrdenViewImpl)
  {
    path: 'cambio-orientacion',
    title: '📝 Cambios en Orientaciones',
    component: CambioOrientacionComponent
  },
  // Aprobar y Rechazar Informes (HU1232313 - Migración desde FilterInformesEnviadosViewImpl)
  {
    path: 'aprobar-rechazar-informes',
    title: '📋 Consultar Informes Enviados Preaprobados',
    component: AprobarRechazarInformesComponent
  },
  // Servicios Entregados - Listar Facturas (CA1 - Migración desde FacturasNoEnviadasViewImpl)
  {
    path: 'servicios-entregados',
    title: '📋 Servicios Entregados',
    component: ServiciosEntregadosComponent
  },
  // Servicios Entregados - Rectificar Transporte (recurso registrado en hasAccess)
  {
    path: 'servicios-entregados/rectificar-transporte',
    redirectTo: 'servicios-entregados',
    pathMatch: 'full'
  },
  // Informacion de Tareas (HU1 MVP3)
  {
    path: 'informacion-tareas',
    title: '📎 Informacion de Tareas',
    component: InformacionTareasComponent
  },
  {
    path: 'informacion-tareas/detalle/:id',
    title: '📎 Detalle de Orden',
    component: InformacionTareasDetalleComponent
  },
  {
    path: 'informacion-tareas/detalle',
    title: '📎 Detalle de Orden',
    component: InformacionTareasDetalleComponent
  },
  // Informacion Empresa (Migracion desde InformacionEmpresaViewImpl.java)
  {
    path: 'informacion-empresa',
    title: '🏢 Informacion Empresa',
    component: InformacionEmpresaComponent
  },
  {
    path: 'informacion-empresa/historial-gestiones',
    title: '📆 Informacion Empresa - Historial de Gestiones',
    component: InformacionEmpresaHistorialGestionesComponent
  },
  {
    path: 'informacion-empresa/ordenado-consultoria',
    title: '📦 Informacion Empresa - Ordenado por Consultoria',
    component: InformacionEmpresaOrdenadoConsultoriaComponent
  },
  {
    path: 'informacion-empresa/ordenado-consultoria/detalle',
    title: '🗄 Informacion Empresa - Detalle Ordenado por Consultoria',
    component: InformacionEmpresaDetalleOrdenadoConsultoriaComponent
  },
  {
    path: 'informacion-empresa/citas-programadas',
    title: '📅 Informacion Empresa - Citas Programadas',
    component: InformacionEmpresaCitasProgramadasComponent
  },
  {
    path: 'informacion-empresa/citas-programadas/detalle',
    title: '🗄 Informacion Empresa - Detalle Citas Programadas',
    component: InformacionEmpresaDetalleCitasProgramadasComponent
  },
  {
    path: 'informacion-empresa/servicios-entregados',
    title: '🎥 Informacion Empresa - Servicios Entregados',
    component: InformacionEmpresaServiciosEntregadosComponent
  },
  {
    path: 'informacion-empresa/servicios-entregados/detalle',
    title: '🗄 Informacion Empresa - Detalle Servicios Entregados',
    component: InformacionEmpresaDetalleServiciosEntregadosComponent
  },
  {
    path: 'informacion-empresa/informes-entregados',
    title: '📿 Informacion Empresa - Informes Entregados',
    component: InformacionEmpresaInformesEntregadosComponent
  },
  {
    path: 'informacion-empresa/informes-entregados/detalle',
    title: '🗄 Informacion Empresa - Detalle Informes Entregados',
    component: InformacionEmpresaDetalleInformesEntregadosComponent
  },
  {
    path: 'home',
    title: 'Inicio',
    component: HomeComponent,
    canActivate: SecurityLoaders.loadGuards()
  },
  {
    path: 'test-imports',
    title: 'Prueba @sura-ui Individual',
    component: FinalTestComponent
  },
  {
    path: 'test-unified',
    title: 'Prueba Librería Unificada',
    component: TestUnifiedLibraryComponent
  },
  {
    path: 'simple-test',
    title: 'Prueba Simple @sura-ui',
    component: SimpleTestComponent
  },
  {
    path: 'handbook',
    loadChildren: () =>
      import('./handbook/handbook.routes').then((s) => s.handbookRoutes),
    canActivate: SecurityLoaders.loadGuards()
  }
];

/**
 * Rutas de la aplicación
 */
const BASE_ROUTES: Routes = [
  {
    path: '',
    redirectTo: 'dashboard',
    pathMatch: 'full'
  },
  {
    path: 'access-denied',
    title: 'GIROP Access Denied',
    component: AccessDeniedComponent
  },
  {
    path: 'not-found',
    title: 'Página no encontrada',
    component: NotFoundComponent
  },
  {
    path: '',
    component: LayoutComponent,
    children: PRIVATE_ROUTES
  }
];

export const APP_ROUTES: Routes = buildAppRoutes();
